// ── Cache layer ────────────────────────────────────────────────────────────────
// localStorage-backed cache with TTL.
// Prevents re-reading Firestore on every page load.
// Cache keys are namespaced by channelId.

const CACHE_VERSION = "v2"; // bump this to invalidate all caches on deploy
const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes

function cacheKey(namespace, id = "") {
  return `cc_${CACHE_VERSION}_${namespace}${id ? "_" + id : ""}`;
}

export const cache = {
  get(namespace, id = "") {
    try {
      const raw = localStorage.getItem(cacheKey(namespace, id));
      if (!raw) return null;
      const { data, expiresAt } = JSON.parse(raw);
      if (Date.now() > expiresAt) {
        localStorage.removeItem(cacheKey(namespace, id));
        return null;
      }
      return data;
    } catch { return null; }
  },

  set(namespace, id = "", data, ttlMs = DEFAULT_TTL_MS) {
    try {
      localStorage.setItem(cacheKey(namespace, id), JSON.stringify({
        data, expiresAt: Date.now() + ttlMs
      }));
    } catch (e) {
      // localStorage full — clear old CC entries and retry
      if (e.name === "QuotaExceededError") {
        this.clearAll();
        try {
          localStorage.setItem(cacheKey(namespace, id), JSON.stringify({
            data, expiresAt: Date.now() + ttlMs
          }));
        } catch { /* give up */ }
      }
    }
  },

  invalidate(namespace, id = "") {
    try { localStorage.removeItem(cacheKey(namespace, id)); } catch {}
  },

  invalidateChannel(channelId) {
    // Invalidate all cache entries for a specific channel
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(`cc_${CACHE_VERSION}_`) && k.includes(channelId))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
  },

  clearAll() {
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith("cc_"))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
  },
};

// ── Quota guard ────────────────────────────────────────────────────────────────
// When Firestore returns quota-exhausted, stop all reads for 1 hour.
// Prevents retry storms that burn quota even faster.

const QUOTA_BLOCK_KEY = "cc_quota_blocked_until";
const QUOTA_BLOCK_DURATION = 60 * 60 * 1000; // 1 hour

export const quotaGuard = {
  isBlocked() {
    try {
      const until = parseInt(localStorage.getItem(QUOTA_BLOCK_KEY) || "0");
      return Date.now() < until;
    } catch { return false; }
  },

  block() {
    try {
      const until = Date.now() + QUOTA_BLOCK_DURATION;
      localStorage.setItem(QUOTA_BLOCK_KEY, String(until));
      console.warn("[CC] Firestore quota exhausted — reads paused for 1 hour");
    } catch {}
  },

  unblock() {
    try { localStorage.removeItem(QUOTA_BLOCK_KEY); } catch {}
  },

  timeRemaining() {
    try {
      const until = parseInt(localStorage.getItem(QUOTA_BLOCK_KEY) || "0");
      return Math.max(0, until - Date.now());
    } catch { return 0; }
  },

  // Wrap any Firestore call with quota guard
  async guard(fn) {
    if (this.isBlocked()) {
      const mins = Math.ceil(this.timeRemaining() / 60000);
      throw new Error(`Firestore reads paused due to quota limit. Resumes in ~${mins} min.`);
    }
    try {
      return await fn();
    } catch (e) {
      if (e.code === "resource-exhausted" || e.message?.includes("Quota exceeded")) {
        this.block();
        throw new Error("Daily read quota reached. App will resume reading Firestore in 1 hour. Your data is safe.");
      }
      throw e;
    }
  }
};

// ── Write debouncer ────────────────────────────────────────────────────────────
// Batches rapid writes (e.g. checkbox ticks) into a single Firestore write.
// Prevents a user clicking 10 checkboxes from creating 10 writes.

export class WriteDebouncer {
  constructor(flushFn, delayMs = 1500) {
    this.flushFn = flushFn;
    this.delayMs = delayMs;
    this.pending = {};
    this.timer = null;
  }

  set(key, value) {
    this.pending[key] = value;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), this.delayMs);
  }

  async flush() {
    if (Object.keys(this.pending).length === 0) return;
    const batch = { ...this.pending };
    this.pending = {};
    this.timer = null;
    await this.flushFn(batch);
  }

  destroy() {
    clearTimeout(this.timer);
    this.flush(); // flush any pending writes on unmount
  }
}

// ── Usage metering ─────────────────────────────────────────────────────────────
// Tracks per-channel Firestore read/write counts for billing dashboard.
// Stored in localStorage — no Firestore needed for this.

const METER_KEY = "cc_usage_meter";

export const usageMeter = {
  _load() {
    try { return JSON.parse(localStorage.getItem(METER_KEY) || "{}"); } catch { return {}; }
  },
  _save(data) {
    try { localStorage.setItem(METER_KEY, JSON.stringify(data)); } catch {}
  },

  record(channelId, operation, count = 1) {
    const data = this._load();
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
    if (!data[today]) data[today] = {};
    if (!data[today][channelId]) data[today][channelId] = { reads: 0, writes: 0 };
    data[today][channelId][operation] += count;
    // Keep only last 30 days
    const keys = Object.keys(data).sort();
    if (keys.length > 30) delete data[keys[0]];
    this._save(data);
  },

  getSummary(days = 14) {
    const data = this._load();
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
      const dayData = data[key] || {};
      const totalReads = Object.values(dayData).reduce((s, c) => s + (c.reads || 0), 0);
      const totalWrites = Object.values(dayData).reduce((s, c) => s + (c.writes || 0), 0);
      result.push({ date: key, reads: totalReads, writes: totalWrites, channels: dayData });
    }
    return result;
  },

  // Estimate monthly Firestore cost in USD
  estimateCost(days = 30) {
    const summary = this.getSummary(days);
    const totalReads = summary.reduce((s, d) => s + d.reads, 0);
    const totalWrites = summary.reduce((s, d) => s + d.writes, 0);
    // Blaze pricing: $0.06 per 100K reads, $0.18 per 100K writes
    // First 50K reads/day and 20K writes/day are free
    const freeReads = 50000 * days;
    const freeWrites = 20000 * days;
    const billableReads = Math.max(0, totalReads - freeReads);
    const billableWrites = Math.max(0, totalWrites - freeWrites);
    const cost = (billableReads / 100000) * 0.06 + (billableWrites / 100000) * 0.18;
    return { totalReads, totalWrites, billableReads, billableWrites, estimatedUSD: cost.toFixed(4) };
  }
};
